import { motion, useReducedMotion } from 'motion/react';
import { SECTION_TOOLS } from './toolIcons';
import { SectionId, sectionById } from './sections';
import { ToolPicker } from './ToolPicker';
import { BrushPanel, WidgetOptionsPanel } from './panels/BrushPanel';
import { ColorPanel } from './panels/ColorPanel';
import { CharPalettePanel } from './panels/CharPalettePanel';
import { LayersPanel } from './panels/LayersPanel';
import { VariablesPanel } from './panels/VariablesPanel';
import { ComponentsPanel } from './panels/ComponentsPanel';
import { AnsiInspectorPanel, CompatibilityPanel, InspectorPanel } from './panels/InspectorPanel';
import { DocumentPanel } from './panels/DocumentPanel';
import { SelectionPanel, GradientPanel } from './panels/SelectionPanel';
import { AnimationPanel } from './panels/AnimationPanel';
import { TemplatesPanel } from './panels/TemplatesPanel';
import { TextPanel } from './panels/TextPanel';
import { TextGenPanel } from './panels/TextGenPanel';

/**
 * The contextual panel beside the rail.
 *
 * Only the controls that belong to the active section are mounted, which keeps
 * the column short enough to scan and avoids paying to render ten panels that
 * nobody is looking at.
 */
export function SidePanel({ section }: { section: SectionId }): JSX.Element {
  const meta = sectionById(section);
  const tools = SECTION_TOOLS[section];
  // Honour the system setting — the motion here is decoration, and for anyone
  // who has asked for less of it the swap should simply be instant.
  const still = useReducedMotion();

  return (
    <aside className="side">
      <header className="side-head">
        <h2>{meta.title}</h2>
        <p>{meta.description}</p>
      </header>
      {/*
        Changing the key remounts the column, and the new one animates in. Only
        the entrance is animated: an exit animation would have to finish before
        the replacement could mount, which puts a delay between the click and
        the controls appearing — the opposite of what the motion is for.
      */}
      <motion.div
        key={section}
        className="side-body"
        initial={still ? false : { opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.16, ease: [0.2, 0.9, 0.3, 1] }}
      >
        {tools ? <ToolPicker tools={tools} /> : null}
        {renderSection(section)}
      </motion.div>
    </aside>
  );
}

function renderSection(section: SectionId): JSX.Element {
  switch (section) {
    case 'draw':
      return (
        <>
          <BrushPanel />
          <CharPalettePanel />
        </>
      );
    case 'select':
      return <SelectionPanel />;
    case 'shapes':
      return (
        <>
          <BrushPanel />
          <CharPalettePanel />
        </>
      );
    case 'text':
      return (
        <>
          <TextGenPanel />
          <TextPanel />
        </>
      );
    case 'elements':
      return <WidgetOptionsPanel />;
    case 'color':
      return (
        <>
          <ColorPanel />
          <GradientPanel />
        </>
      );
    case 'layers':
      return <LayersPanel />;
    case 'animate':
      return <AnimationPanel />;
    case 'templates':
      return <TemplatesPanel />;
    case 'data':
      return (
        <>
          <VariablesPanel />
          <ComponentsPanel />
        </>
      );
    case 'settings':
      return (
        <>
          <DocumentPanel />
          <InspectorPanel />
          <AnsiInspectorPanel />
          <CompatibilityPanel />
        </>
      );
  }
}
